import { Pager } from "@d12labs/ui/pager";
import { cn } from "@d12labs/utils";
import { Copy } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { harnessLabel } from "../aggregate";
import {
	DEFAULT_GAP_SORT,
	firstSortDir,
	type GapSortKey,
	KIND_LABEL,
	pageSlice,
	type SortDir,
	sortGapRows,
} from "../gaps-view";
import type { GapRow } from "../overview";
import { PAGE_SIZE } from "./sessions-table";
import { HarnessDot } from "./spend-summary";

const COLUMNS: { key: GapSortKey; label: string; align?: "right"; width: string }[] = [
	{ key: "kind", label: "Kind", width: "w-[96px]" },
	{ key: "path", label: "File", width: "" },
	{ key: "harness", label: "Harness", width: "w-[124px]" },
	{ key: "models", label: "Model(s)", width: "w-[200px]" },
	{ key: "turns", label: "Turns", align: "right", width: "w-[80px]" },
];

async function copyPath(path: string) {
	try {
		await navigator.clipboard.writeText(path);
		toast.success("Path copied");
	} catch {
		toast.error("Could not copy the path");
	}
}

/**
 * Filtered gap rows, 36px rows, sortable, paginated. A new `resetKey` (the
 * filter set) returns it to page one and keeps the chosen sort.
 */
export function GapsTable({ rows, resetKey }: { rows: GapRow[]; resetKey: string }) {
	const [sortKey, setSortKey] = useState<GapSortKey>(DEFAULT_GAP_SORT.key);
	const [sortDir, setSortDir] = useState<SortDir>(DEFAULT_GAP_SORT.dir);
	const [page, setPage] = useState(0);
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset-on-key pattern
	useEffect(() => setPage(0), [resetKey]);

	const view = pageSlice(sortGapRows(rows, sortKey, sortDir), page, PAGE_SIZE);

	const sortBy = (key: GapSortKey) => {
		if (key === sortKey) setSortDir((dir) => (dir === -1 ? 1 : -1));
		else {
			setSortKey(key);
			setSortDir(firstSortDir(key));
		}
		setPage(0);
	};

	return (
		<section
			aria-label="Gaps"
			className="overflow-hidden rounded-lg border border-hairline bg-surface-1"
		>
			<div className="overflow-x-auto">
				<table className="w-full min-w-[760px] table-fixed text-body-sm">
					<thead>
						<tr className="h-[30px] border-b border-hairline text-left text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">
							{COLUMNS.map((column, i) => (
								<th
									key={column.key}
									scope="col"
									aria-sort={
										sortKey === column.key ? (sortDir < 0 ? "descending" : "ascending") : undefined
									}
									className={cn(
										"font-medium",
										column.width,
										i === 0 ? "pl-4 pr-3" : "px-3",
										column.align === "right" && "text-right",
									)}
								>
									<button
										type="button"
										onClick={() => sortBy(column.key)}
										className="whitespace-nowrap uppercase outline-none hover:text-foreground focus-visible:text-foreground focus-visible:underline"
									>
										{column.label}
										{sortKey === column.key ? (sortDir < 0 ? " ↓" : " ↑") : ""}
									</button>
								</th>
							))}
							<th scope="col" className="w-[48px] pl-3 pr-4">
								<span className="sr-only">Copy</span>
							</th>
						</tr>
					</thead>
					<tbody>
						{view.rows.length === 0 ? (
							<tr className="h-9">
								<td colSpan={COLUMNS.length + 1} className="px-4 text-ink-subtle">
									No gaps match
								</td>
							</tr>
						) : (
							view.rows.map((row) => {
								const models = row.models.join(", ");
								return (
									<tr
										key={row.key}
										className="h-9 border-b border-hairline last:border-0 hover:bg-surface-2"
									>
										<td
											className={cn(
												"pl-4 pr-3 text-[11px] font-medium",
												row.kind === "unpriced" ? "text-warning" : "text-ink-subtle",
											)}
										>
											{KIND_LABEL[row.kind]}
										</td>
										<td
											className="truncate px-3 font-mono text-[11.5px] text-ink-muted"
											title={row.path}
										>
											{row.path}
										</td>
										<td className="px-3">
											<span className="inline-flex items-center gap-1.5 text-ink-muted">
												<HarnessDot harness={row.harness} />
												{harnessLabel(row.harness)}
											</span>
										</td>
										<td className="truncate px-3 text-ink-subtle" title={models || undefined}>
											{models || "—"}
										</td>
										<td className="px-3 text-right tabular-nums text-ink-muted">
											{row.turns.toLocaleString("en-US")}
										</td>
										<td className="pl-3 pr-4">
											<button
												type="button"
												onClick={() => copyPath(row.path)}
												aria-label={`Copy path ${row.path}`}
												title="Copy path"
												className="flex size-6 items-center justify-center rounded text-ink-subtle outline-none hover:bg-surface-3 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50"
											>
												<Copy className="size-3.5" />
											</button>
										</td>
									</tr>
								);
							})
						)}
					</tbody>
				</table>
			</div>
			{/* view.page is 0-based; <Pager> is 1-based — convert here, never inside it. */}
			<Pager
				className="border-t border-hairline px-4 py-2.5 text-meta text-ink-subtle"
				page={view.page + 1}
				pageCount={view.pages}
				onPageChange={(p) => setPage(p - 1)}
				variant="secondary"
				label={
					<span className="tabular-nums">
						Showing {view.start.toLocaleString("en-US")}–{view.end.toLocaleString("en-US")} of{" "}
						{rows.length.toLocaleString("en-US")}
					</span>
				}
			/>
		</section>
	);
}
